import { CommonModule } from '@angular/common';
import { Component, inject, Inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CountryService } from '@services/country.service';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { AuthService } from 'src/app/Auth/AuthService';
import { PageAccessService } from 'src/app/Auth/page-access.service';
import { environment } from '@env';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, CommonModule, TranslateModule],
  providers: [],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', '../../../../auth.scss'],
})
export class LoginComponent {
  baseUrl = environment.baseUrl;

  LoginForm: FormGroup;
  pageService = inject(PagesService);
  countryService = inject(CountryService);
  private pageAccess = inject(PageAccessService);
  isPasswordVisible = false;

  ngOnInit() {
    // Hide loader when login page loads (after logout)
    this.sharedService.hideLoader();
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private sharedService: SharedService
  ) {
    this.markPaymentAsChecked()
    this.LoginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });
  }

onSubmit() {
  this.sharedService.showLoader();

  if (this.LoginForm.valid) {
    this.authService.login('LOGIN', this.LoginForm.value).subscribe({
      next: (response: any) => {
        this.authService.setToken(response);
        this.afterLogin(response);
      },
      error: (err) => this.showLoginError(err),
    });
  } else {
    this.LoginForm.markAllAsTouched();
    this.sharedService.hideLoader();
  }
}

private showLoginError(err: any) {
        this.sharedService.hideLoader();

        // ✅ Extract backend message directly
        const backendMessage =
          err?.error?.message ||
          err?.message ||
          'Login failed. Please try again.';

        // ✅ Incomplete signup — pick up at the step that was left (company or verification)
        const resumeAt = this.authService.resumeOnboarding(err);
        if (resumeAt) {
          this.sharedService.createNotification(
            'warning',
            resumeAt === '/auth/company'
              ? "You haven't added a company yet."
              : 'Please verify your account.'
          );
          this.router.navigate([resumeAt]);
          return;
        }

        // ✅ Auth related (401 / 403)
        if (err?.status === 401 || err?.status === 403) {
          this.sharedService.createNotification('error', backendMessage);
          return;
        }

        // ✅ All other errors
        this.sharedService.createNotification('error', backendMessage);
}

private afterLogin(response: any) {
        const user = response.user;

        // ✅ Fetch user company + image
        this.pageService.getAll('GET_USER_COMPANY').subscribe({
          next: (res: any) => {
            const userData = res[0] || {};
            const imgUrl =
              userData.business_logo?.[0]?.url || 'assets/images/profile.png';
            localStorage.setItem('profileImage', imgUrl);
            this.sharedService.updateProfileImage(imgUrl);
          },
          error: () => {
            this.sharedService.updateProfileImage('assets/images/profile.png');
          }
        });

        // ✅ Company check
        if (!user.isCompany || user.isCompany.length === 0) {
          this.sharedService.createNotification(
            'warning',
            "You haven't added a company yet."
          );
          this.sharedService.hideLoader();
          this.router.navigate(['/auth/company']);
          return;
        }

        // ✅ Email verification check
        if (!user.verified) {
          this.sharedService.createNotification(
            'warning',
            'Please verify your account.'
          );
          this.sharedService.hideLoader();
          this.router.navigate(['/auth/send-otp']);
          return;
        }

        // ✅ Sub-user with no role, or a role with no pages: stop here with a popup instead of
        // opening a dashboard they cannot use (QA #289 / #290).
        if (user.is_sub_user === true) {
          this.pageAccess.reset();
          this.pageAccess.load().subscribe((access) => {
            if (access.noAccess) {
              this.pageAccess.signOutNoAccess();
              return;
            }
            this.router.navigate(['/home/quick-actions']);
          });
          return;
        }

        // ✅ Success: Redirect to home
        // Note: Don't hide loader here - let layout component hide it after it loads
        this.router.navigate(['/home/quick-actions']);
}

  markPaymentAsChecked() {
    this.route.queryParams.subscribe((params) => {
      const sessionId = params['session_id'];
      if (sessionId) {
        // ✅ Remove session_id from the URL state
        const baseHash = window.location.hash.split('?')[0];
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${baseHash}`
        );
        const data = { session_id: sessionId };
        this.pageService.update('CHECK_PAYMENT', data).subscribe({
          next: (response: any) => {
            const isSuccess = response?.data?.isSuccess || response?.isSuccess || response?.status === 'paid' || response?.data?.status === 'paid';
            if (isSuccess || !response?.error) {
              this.router.navigate(['/auth/payment-approved'], {
                queryParams: { session_id: sessionId }
              });
            }
          },
        });
      }
    });
  }


  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }



  loginWithFacebook() {
    this.authService
      .signInWithFacebook()
      .then((res) => console.log(res.user))
      .catch((err) => console.error(err));
  }

  private decodeToken(token: string) {
    return JSON.parse(atob(token.split('.')[1]));
  }
  // Google sign-in is deliberately absent. A business Google login would be a token-issuing action
  // open to the world, and nothing links a Google identity to a business account, so there is
  // nothing it could safely match. The page no longer loads Google Identity Services; SOCIAL_LOGIN
  // stays out of the gateway map until an account-linking flow exists.

}
